import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { toast } from 'react-toastify';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import EntitySearchPicker, { getEntityDisplayName, getEntityId } from '../components/EntitySearchPicker';
import { loadDropdownData, addAddressToEntity } from '../customer/onboardingApi';
import StepBar from '../components/StepBar';

// ── Steps ─────────────────────────────────────────────────────
const STEPS = [
    { key: 'search', label: 'Find Entity' },
    { key: 'address', label: 'Address' },
    { key: 'review', label: 'Review' },
    { key: 'done', label: 'Done' },
];

/**
 * Blank address initialization
 */
function blankAddress(activeStatusId) {
    return {
        id: crypto.randomUUID(),
        address_type_id: '',
        name: '',
        street_address1: '',
        street_address2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        latitude: '',
        longitude: '',
        status_type_id: activeStatusId || '',
    };
}

/**
 * Get the ID of the "Active" status from dropdown data
 */
function getActiveStatusId(dropdowns) {
    if (!dropdowns || !dropdowns.statusTypes) return '';
    return (
        dropdowns.statusTypes.find(
            (s) => s?.StatusType?.status_name?.toLowerCase() === 'active',
        )?.status_type_id || dropdowns.statusTypes[0]?.status_type_id || ''
    );
}

export default function AddAddressWizard({ onComplete, onCancel, initialEntity = null } = {}) {
    const [step, setStep] = useState('search');
    // A device claim passes its selected customer so it need not be found again.
    const [selectedEntity, setSelectedEntity] = useState(initialEntity);
    const [dropdowns, setDropdowns] = useState(null);
    const [address, setAddress] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    // Load dropdowns on mount
    useEffect(() => {
        loadDropdownData().then(d => {
            setDropdowns(d);
            setAddress(blankAddress(getActiveStatusId(d)));
        }).catch(() => toast.error('Failed to load form options'));
    }, []);

    const updateAddress = (field, value) => {
        setAddress(prev => prev ? { ...prev, [field]: value } : prev);
    };

    const handleAutocomplete = async (value) => {
        try {
            const results = await geocodeByPlaceId(value.place_id);
            if (results && results.length > 0) {
                const placeData = results[0];
                const { address_components, geometry } = placeData;

                const newAddressObj = {
                    ...address,
                    street_address1: `${address_components.find(comp => comp.types.includes('street_number'))?.long_name || ""} ${address_components.find(comp => comp.types.includes('route'))?.long_name || ""}`,
                    street_address2: address_components.find(comp => comp.types.includes('neighborhood'))?.long_name || "",
                    city: address_components.find(comp => comp.types.includes('locality'))?.long_name || "",
                    state: address_components.find(comp => comp.types.includes('administrative_area_level_1'))?.short_name || "",
                    postal_code: address_components.find(comp => comp.types.includes('postal_code'))?.long_name || "",
                    country: address_components.find(comp => comp.types.includes('country'))?.long_name || "",
                    latitude: geometry?.location.lat() || "",
                    longitude: geometry?.location.lng() || "",
                };

                setAddress(prev => prev ? newAddressObj : prev);
            }
        } catch (error) {
            console.error('Error fetching geolocation:', error);
            toast.error('Failed to resolve address details');
        }
    };

    const canProceedFromAddress = () => {
        if (!address) return false;
        return (
            address?.name?.trim() !== '' &&
            address?.street_address1?.trim() !== '' &&
            address?.city?.trim() !== '' &&
            address?.state?.trim() !== '' &&
            address?.address_type_id !== ''
        );
    };

    const handleSubmit = async () => {
        if (!address || !selectedEntity) return;
        setSubmitting(true);
        try {
            const entityId = getEntityId(selectedEntity);
            const payload = {
                address_type_id: Number(address.address_type_id),
                entity_id: entityId,
                name: address.name,
                street_address1: address.street_address1,
                street_address2: address.street_address2,
                city: address.city,
                state: address.state,
                postal_code: address.postal_code,
                country: address.country,
                latitude: address.latitude,
                longitude: address.longitude,
                address_main_type: 'entity',
                status_type_id: address.status_type_id
            }
            const res = await addAddressToEntity(payload);
            setResult(res);
            setStep('done');
            toast.success('Address added successfully!');
            if (onComplete) {
                try {
                    onComplete({ entityId, response: res });
                } catch (callbackError) {
                    console.error('Location saved; installation return failed', callbackError);
                    toast.error('Location saved, but returning to installation failed. Do not create it again.');
                }
            }
        } catch (err) {
            toast.error(err.message || 'Failed to add address');
        } finally {
            setSubmitting(false);
        }
    };
    const addressTypes = dropdowns?.addressTypes ?? [];
    const statusOptions = dropdowns?.statusTypes ?? [];

    return (
        <MDBox sx={{ mx: 'auto' }}>
            {step !== 'done' && <StepBar current={step} STEPS={STEPS} />}

            {/* ── Step 1: Search entity ── */}
            {step === 'search' && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '12px', p: 3 }}>
                    <MDTypography variant="h6" color="white" fontWeight="bold">Find the Entity</MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.6} sx={{ display: 'block', mb: 3 }}>
                        Search for the customer or entity you want to add an address to.
                    </MDTypography>

                    <EntitySearchPicker
                        selectedEntity={selectedEntity}
                        onSelect={setSelectedEntity}
                    />

                    <MDBox mt={4} display="flex" justifyContent="flex-end">
                        <MDButton
                            disabled={!selectedEntity}
                            onClick={() => setStep('address')}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Continue <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 2: Address form ── */}
            {step === 'address' && address && dropdowns && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '12px', p: 3 }}>
                    <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="fa fa-map-marker-alt" style={{ color: '#FACC15' }} /> Address Details
                    </MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.6} sx={{ display: 'block', mb: 3 }}>
                        Adding address for: <span style={{ color: 'white', fontWeight: 500 }}>{selectedEntity ? getEntityDisplayName(selectedEntity) : ''}</span>
                    </MDTypography>

                    <MDBox component="form" className="space-y-4">
                        {/* Address name */}
                        <MDBox mb={2}>
                            <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                                Address Label <span style={{ color: '#F87171' }}>*</span>
                            </MDTypography>
                            <input
                                type="text"
                                value={address.name}
                                onChange={e => updateAddress('name', e.target.value)}
                                placeholder="e.g. Main Office, Farm Site, Tank Yard"
                                style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                            />
                        </MDBox>

                        {/* Address type */}
                        <MDBox mb={2}>
                            <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                                Address Type <span style={{ color: '#F87171' }}>*</span>
                            </MDTypography>
                            <select
                                value={address.address_type_id}
                                onChange={e => updateAddress('address_type_id', e.target.value)}
                                style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                            >
                                <option value="">— Select type —</option>
                                {addressTypes.map((t) => (
                                    <option key={t.address_type_id} value={t.address_type_id}>{t.type_name}</option>
                                ))}
                            </select>
                        </MDBox>

                        {/* Street address autocomplete */}
                        <MDBox mb={2}>
                            <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                                Street Address <span style={{ color: '#F87171' }}>*</span>
                            </MDTypography>
                            <div className="places-autocomplete-container">
                                <GooglePlacesAutocomplete
                                    apiKey={process.env.REACT_APP_MAP_API_KEY}
                                    onSelect={handleAutocomplete}
                                />

                                <style>{`
                              .places-autocomplete-container {
                                position: relative;
                              }

                              .google-places-autocomplete__input {
                                width: 100%;
                                padding: 2px;
                                border: 1px solid #ccc;
                                border-radius: 4px;
                              }

                              .google-places-autocomplete__suggestions-container {
                                position: absolute;
                                top: calc(100% + 5px);
                                left: 0;
                                width: 100%;
                                background-color: #fff;
                                border: 1px solid #ccc;
                                border-radius: 4px;
                                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                                z-index: 1000;
                                max-height: 100px;
                                overflow-y: auto;
                              }

                              .google-places-autocomplete__suggestion {
                                padding: 8px 16px;
                                cursor: pointer;
                              }

                              .google-places-autocomplete__suggestion:hover {
                                background-color: #f0f0f0;
                              }

                              .google-places-autocomplete__suggestion--active {
                                background-color: #e0e0e0;
                              }
                        `}</style>
                            </div>
                        </MDBox>

                        {/* Suite / Apt */}
                        <MDBox mb={2}>
                            <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Suite / Apt</MDTypography>
                            <input
                                type="text"
                                value={address.street_address2}
                                onChange={e => updateAddress('street_address2', e.target.value)}
                                placeholder="Optional"
                                style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                            />
                        </MDBox>

                        {/* City / State / Zip */}
                        <MDBox display="flex" gap={2} mb={2}>
                            <MDBox flex={1}>
                                <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>City <span style={{ color: '#F87171' }}>*</span></MDTypography>
                                <input
                                    type="text"
                                    value={address.city}
                                    onChange={e => updateAddress('city', e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                                />
                            </MDBox>
                            <MDBox width="80px">
                                <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>State <span style={{ color: '#F87171' }}>*</span></MDTypography>
                                <input
                                    type="text"
                                    value={address.state}
                                    onChange={e => updateAddress('state', e.target.value)}
                                    maxLength={2}
                                    placeholder="WI"
                                    style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                                />
                            </MDBox>
                            <MDBox width="100px">
                                <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>ZIP</MDTypography>
                                <input
                                    type="text"
                                    value={address.postal_code}
                                    onChange={e => updateAddress('postal_code', e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                                />
                            </MDBox>
                        </MDBox>

                        {/* Status */}
                        <MDBox mb={2}>
                            <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Status</MDTypography>
                            <select
                                value={address.status_type_id}
                                onChange={e => updateAddress('status_type_id', e.target.value)}
                                style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px', color: 'white', fontSize: '0.875rem' }}
                            >
                                {statusOptions.map((s) => (
                                    <option key={s.status_type_id} value={s.status_type_id}>
                                        {s.StatusType?.status_name}
                                    </option>
                                ))}
                            </select>
                        </MDBox>
                    </MDBox>

                    {/* Nav */}
                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep('search')}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Back
                        </MDButton>
                        {onCancel && <MDButton variant="outlined" color="secondary"
                            disabled={submitting} onClick={onCancel}>
                            Back to device installation
                        </MDButton>}
                        <MDButton
                            disabled={!canProceedFromAddress()}
                            onClick={() => setStep('review')}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Review <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 3: Review ── */}
            {step === 'review' && address && selectedEntity && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '12px', p: 3 }}>
                    <MDTypography variant="h6" color="white" fontWeight="bold" mb={3}>Review & Confirm</MDTypography>

                    {/* Entity */}
                    <MDBox mb={3} p={2} sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px' }}>
                        <MDTypography variant="caption" color="white" fontWeight="bold" textTransform="uppercase" sx={{ opacity: 0.5, display: 'block', mb: 1 }}>Entity</MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="white" display="block">{getEntityDisplayName(selectedEntity)}</MDTypography>
                        <MDTypography variant="caption" color="white" opacity={0.6}>ID #{getEntityId(selectedEntity)}</MDTypography>
                    </MDBox>

                    {/* Address */}
                    <MDBox p={2} sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid gray', borderRadius: '8px' }}>
                        <MDTypography variant="caption" color="white" fontWeight="bold" textTransform="uppercase" sx={{ opacity: 0.5, display: 'block', mb: 1 }}>New Address</MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="white" display="block">{address.name}</MDTypography>
                        <MDTypography variant="button" color="white" display="block">{address.street_address1}</MDTypography>
                        {address.street_address2 && <MDTypography variant="button" color="white" display="block">{address.street_address2}</MDTypography>}
                        <MDTypography variant="button" color="white" display="block">{address.city}, {address.state} {address.postal_code}</MDTypography>
                        {address.latitude && address.longitude && (
                            <MDTypography variant="caption" color="white" opacity={0.5} sx={{ mt: 1, display: 'block' }}>
                                {Number(address.latitude).toFixed(5)}, {Number(address.longitude).toFixed(5)}
                            </MDTypography>
                        )}
                    </MDBox>

                    {/* Nav */}
                    <MDBox mt={4} display="flex" justifyContent="space-between">
                        <MDButton
                            onClick={() => setStep('address')}
                            variant="outlined"
                            color="secondary"
                            sx={{ gap: 1 }}
                        >
                            <i className="fa fa-chevron-left" /> Edit
                        </MDButton>
                        {onCancel && (
                            <MDButton
                                disabled={submitting}
                                onClick={onCancel}
                                variant="outlined"
                                color="secondary"
                            >
                                Back to device installation
                            </MDButton>
                        )}
                        <MDButton
                            disabled={submitting}
                            onClick={handleSubmit}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            {submitting ? (
                                <><i className="fa fa-spinner fa-spin" /> Saving…</>
                            ) : (
                                <>Confirm & Save <i className="fa fa-chevron-right" /></>
                            )}
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 4: Done ── */}
            {step === 'done' && selectedEntity && address && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid #49a3f1', borderRadius: '12px', p: 4, textAlign: 'center' }}>
                    <i className="fa fa-check-circle" style={{ color: '#49a3f1', fontSize: '3rem', margin: '0 auto 1.5rem', display: 'block' }} />
                    <MDTypography variant="h5" color="white" fontWeight="bold" mb={1}>Address Added!</MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.7} mb={3} sx={{ display: 'block' }}>
                        <span style={{ color: 'white', fontWeight: 'bold' }}>{address.name}</span> was added to{' '}
                        <span style={{ color: 'white', fontWeight: 'bold' }}>{getEntityDisplayName(selectedEntity)}</span>.
                    </MDTypography>
                    {result?.data?.attributes?.address_id && (
                        <MDTypography variant="caption" color="white" opacity={0.5} mb={4} sx={{ display: 'block' }}>Address ID #{result.data.attributes.address_id}</MDTypography>
                    )}

                    <MDBox display="flex" flexWrap="wrap" gap={2} justifyContent="center" mt={4}>
                        {/* Note: I'm not sure if add-items route exists, but following user code */}
                        {onCancel && <MDButton variant="outlined" color="secondary"
                            disabled={submitting} onClick={onCancel}>
                            Back to device installation
                        </MDButton>}
                        <MDButton
                            component={Link}
                            to="/onboarding"
                            variant="gradient"
                            color="info"
                            size="small"
                        >
                            + Add Items to This Address
                        </MDButton>
                        <MDButton
                            onClick={() => {
                                setStep('search');
                                setSelectedEntity(null);
                                setAddress(dropdowns ? blankAddress(getActiveStatusId(dropdowns)) : null);
                                setResult(null);
                            }}
                            variant="contained"
                            color="secondary"
                            size="small"
                        >
                            Add Another Address
                        </MDButton>
                        <MDButton
                            component={Link}
                            to="/onboarding"
                            variant="outlined"
                            color="white"
                            size="small"
                        >
                            Back to Onboarding
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}
        </MDBox>
    );
}
